import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Descriptions, Drawer, Input, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import moment, { Moment } from 'moment';
import { AdGift, AdGiftFilters, AdGiftResult, GiftStatus, getAdGifts } from '../../service/ad-gift/AdGiftService';

const labels: Record<GiftStatus, { text: string; color: string }> = {
  pending: { text: 'Chờ nhận', color: 'gold' },
  active: { text: 'Đang hưởng quà', color: 'green' },
  ended: { text: 'Đã hết quà', color: 'default' },
  expired: { text: 'Hết hạn nhận', color: 'orange' },
  cancelled: { text: 'Đã hủy', color: 'red' },
};
const displayDate = (value?: string | null) => value && moment(value).isValid() ? moment.utc(value).utcOffset(420).format('DD/MM/YYYY HH:mm') : '—';
const displayName = (gift: AdGift) => [gift.firstName, gift.lastName].filter(part => part && part !== 'null').join(' ') || 'Không có thông tin tài khoản';
const statusTag = (gift: AdGift) => {
  const status = labels[gift.displayStatus];
  return <Tag color={status?.color}>{status?.text || gift.status}</Tag>;
};
const getError = (error: unknown) => {
  const e = error as { response?: { data?: { message?: string } }; message?: string };
  return e?.response?.data?.message || e?.message || 'Không thể tải dữ liệu. Vui lòng thử lại.';
};

function AccountHistory({ account }: { account: AdGift }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AdGiftResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setData(null);
    getAdGifts({ userId: account.userId, page, size: 10 }).then(result => {
      if (active) setData(result);
    }).catch(e => { if (active) setError(getError(e)); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [account.userId, page, version]);
  return <Space direction="vertical" size="middle" style={{ width: '100%' }}>
    <Descriptions column={1} size="small" bordered>
      <Descriptions.Item label="Tài khoản">{account.email || 'Tài khoản không còn tồn tại'}</Descriptions.Item>
      <Descriptions.Item label="Họ tên">{displayName(account)}</Descriptions.Item>
      <Descriptions.Item label="User ID"><Typography.Text copyable>{account.userId}</Typography.Text></Descriptions.Item>
      <Descriptions.Item label="Tổng lượt trúng">{data?.total ?? '—'}</Descriptions.Item>
    </Descriptions>
    {error && <Alert type="error" showIcon message={error} action={<Button onClick={() => setVersion(value => value + 1)}>Thử lại</Button>} />}
    <Table<AdGift> rowKey="giftId" dataSource={data?.docs || []} loading={loading} size="small"
      pagination={{ current: page, pageSize: 10, total: data?.total || 0, onChange: setPage, showSizeChanger: false }}
      columns={[
        { title: 'Ngày trúng', dataIndex: 'selectedAt', render: displayDate },
        { title: 'Trạng thái', render: (_, gift) => statusTag(gift) },
        { title: 'Số ngày tặng', dataIndex: 'durationDays' },
      ]}
      expandable={{ expandedRowRender: gift => <Descriptions column={1} size="small">
        <Descriptions.Item label="Mã quà"><Typography.Text copyable>{gift.giftId}</Typography.Text></Descriptions.Item>
        <Descriptions.Item label="Hạn nhận">{displayDate(gift.claimExpiresAt)}</Descriptions.Item>
        <Descriptions.Item label="Đã nhận lúc">{displayDate(gift.claimedAt)}</Descriptions.Item>
        <Descriptions.Item label="Hết miễn quảng cáo">{displayDate(gift.expiresAt)}</Descriptions.Item>
        <Descriptions.Item label="Đủ thời gian xét lại">{displayDate(gift.nextEligibleAt)}</Descriptions.Item>
        {gift.reason && <Descriptions.Item label="Lý do">{gift.reason}</Descriptions.Item>}
      </Descriptions> }} />
    <Typography.Text type="secondary">Thời gian hiển thị theo giờ Việt Nam (UTC+7). Lịch sử này chỉ gồm quà may mắn, không gồm gói mua hoặc đổi điểm.</Typography.Text>
  </Space>;
}

export default function AdGifts() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<AdGiftFilters['status']>('all');
  const [range, setRange] = useState<[Moment, Moment] | null>(null);
  const [filters, setFilters] = useState<AdGiftFilters>({});
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [data, setData] = useState<AdGiftResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [version, setVersion] = useState(0);
  const [account, setAccount] = useState<AdGift | null>(null);
  const request = useRef(0);
  useEffect(() => {
    const id = ++request.current;
    setLoading(true); setError(''); setData(null);
    getAdGifts({ ...filters, page, size }).then(result => {
      if (request.current === id) setData(result);
    }).catch(e => { if (request.current === id) setError(getError(e)); }).finally(() => { if (request.current === id) setLoading(false); });
    return () => { request.current++; };
  }, [filters, page, size, version]);
  const applyFilters = () => {
    setPage(1);
    setFilters({ email: email.trim() || undefined, status,
      startDate: range?.[0].format('YYYY-MM-DD'), endDate: range?.[1].format('YYYY-MM-DD') });
  };
  const columns: ColumnsType<AdGift> = [
    { title: 'Tài khoản', key: 'account', width: 260, fixed: 'left', render: (_, gift) => <>
      <div style={{ fontWeight: 600 }}>{displayName(gift)}</div><div>{gift.email || 'Không tìm thấy email'}</div>
      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setAccount(gift)}>Xem lịch sử tài khoản</Button>
    </> },
    { title: 'Trạng thái', key: 'status', width: 145, render: (_, gift) => statusTag(gift) },
    { title: 'Ngày tặng', dataIndex: 'durationDays', width: 100, render: value => `${value} ngày` },
    { title: 'Ngày trúng', dataIndex: 'selectedAt', width: 155, render: displayDate },
    { title: 'Hạn nhận', dataIndex: 'claimExpiresAt', width: 155, render: displayDate },
    { title: 'Ngày nhận', dataIndex: 'claimedAt', width: 155, render: displayDate },
    { title: 'Hết miễn quảng cáo', dataIndex: 'expiresAt', width: 165, render: displayDate },
    { title: 'Đủ thời gian xét lại', dataIndex: 'nextEligibleAt', width: 165, render: displayDate },
  ];
  const statistics = [
    ['Tổng lượt trúng', data?.summary.total], ['Tài khoản trúng', data?.summary.accounts],
    ['Chờ nhận', data?.summary.pending], ['Đã nhận', data?.summary.claimed],
    ['Đang hưởng quà', data?.summary.active], ['Đã hết quà', data?.summary.ended],
    ['Hết hạn nhận', data?.summary.expired], ['Đã hủy', data?.summary.cancelled],
  ] as const;
  return <div style={{ padding: '24px 0' }}>
    <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
      <Typography.Title level={3} style={{ margin: 0 }}>Quà may mắn</Typography.Title>
      <Button icon={<ReloadOutlined />} loading={loading} onClick={() => setVersion(value => value + 1)}>Tải lại</Button>
    </Space>
    <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Theo dõi quà miễn quảng cáo. Quà không thay đổi gói Free hoặc quyền tính năng của tài khoản." />
    <Card size="small" style={{ marginBottom: 16 }}>
      <Space wrap>
        <Input aria-label="Tìm email" placeholder="Tìm theo email" value={email} maxLength={254} onChange={e => setEmail(e.target.value)} onPressEnter={applyFilters} style={{ width: 250 }} allowClear />
        <Select aria-label="Trạng thái quà" value={status} onChange={setStatus} style={{ width: 190 }} options={[
          { label: 'Tất cả trạng thái', value: 'all' }, { label: 'Đã nhận (tất cả)', value: 'claimed' },
          ...Object.entries(labels).map(([value, item]) => ({ value, label: item.text })),
        ]} />
        <DatePicker.RangePicker value={range} format="DD/MM/YYYY" placeholder={['Trúng từ ngày', 'Đến ngày']} onChange={value => setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)} />
        <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters}>Lọc</Button>
        <Button onClick={() => { setEmail(''); setStatus('all'); setRange(null); setPage(1); setFilters({}); }}>Xóa bộ lọc</Button>
      </Space>
    </Card>
    {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
    <Typography.Paragraph type="secondary">Tổng quan theo bộ lọc · Ngày trúng và thời gian hiển thị theo giờ Việt Nam (UTC+7).{data && ` Cập nhật lúc ${displayDate(data.asOf)}.`}</Typography.Paragraph>
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      {statistics.map(([title, value]) => <Col xs={12} md={6} key={title}><Card size="small"><Statistic title={title} value={value ?? '—'} /></Card></Col>)}
    </Row>
    {data && <Alert style={{ marginBottom: 16 }} type="success" showIcon message={`Suất đã phát tháng ${data.quota.month}: ${data.quota.count.toLocaleString()} / ${data.quota.limit.toLocaleString()}`}
      description="Bộ đếm phát quà toàn hệ thống, không phụ thuộc bộ lọc và không phải số lượt đã nhận. Đến thời điểm được xét lại không có nghĩa chắc chắn nhận quà." />}
    <Table<AdGift> rowKey="giftId" columns={columns} dataSource={data?.docs || []} loading={loading} scroll={{ x: 1400 }}
      locale={{ emptyText: error ? 'Không tải được dữ liệu' : 'Không có quà phù hợp bộ lọc' }}
      pagination={{ current: page, pageSize: size, total: data?.total || 0, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'],
        showTotal: total => `${total} lượt trúng`, onChange: (nextPage, nextSize) => { setPage(nextSize !== size ? 1 : nextPage); setSize(nextSize); } }} />
    <Drawer title="Lịch sử nhận quà" width="min(760px, 100vw)" visible={!!account} onClose={() => setAccount(null)} destroyOnClose>
      {account && <AccountHistory key={account.userId} account={account} />}
    </Drawer>
  </div>;
}
